import { createStyles, makeStyles, Theme } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  CompositeFilterDescriptor,
  DataResult,
  FilterDescriptor,
  process,
  State,
} from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { setGroupIds } from '@progress/kendo-react-data-tools';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridCustomFilterCellProps,
  GridDataStateChangeEvent,
  GridToolbar,
  isColumnMenuFilterActive,
  isColumnMenuSortActive,
} from '@progress/kendo-react-grid';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { Label } from '@progress/kendo-react-labels';
import React, { useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { NodeDeploymentInfoCSV } from '../../Interfaces/ProductsManagement/NodeDeploymentInfo';
import { ApplicationState } from '../../store';
import { getNodeDeploymentInfo } from '../../store/ProductsManagement/ProductsActions';
import { saveProductsHealthGridState } from '../../store/UserSettings/UserSettingsActions';
import { initialProductsHealthGridDataState } from '../../store/UserSettings/UserSettingsState';
import {
  ACTIVE_THRESHOLD_DATA,
  MAX_DAYS_FOR_ONLINE_STATUS,
} from '../../utils/constants';
import { dateFormat, getDaysDiff, getLocalDateTime } from '../../utils/utils';
import { LocaleInterface, locales } from '../AlertsGrid/GridLocale';
import {
  ColumnMenu,
  CustomCell,
  HeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import DynamicDropDownList from '../DynamicDropDownList';
import { LoadingPanel } from '../LoadingPanel';
import { GetString } from '../StringResourceManager';
import { UpperCaseStringCell } from '../UpperCaseStringCell';
import ProductPackagesHealthDialog from './ProductPackagesHealthDialog';
import ProductsHealthFilter from './ProductsHealthFilter';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarButtonContainer: {
      display: 'grid',
    },
    toolbarButtonGrid: {
      display: 'flex',
    },
    toolbarButton: {
      height: 40,
      marginRight: 9,
    },
    reportingThresholdDropdownContainer: {
      display: 'inline',
      flexDirection: 'row',
      height: 40,
      marginLeft: 5,
      marginTop: 15,
    },
    reportingThresholdDropdownLabel: {
      marginRight: 8,
    },
  }),
);

const processWithGroups = (data: any[], dataState: State): DataResult => {
  if (dataState === undefined) {
    dataState = initialProductsHealthGridDataState;
  }

  let filter: (FilterDescriptor | CompositeFilterDescriptor)[] = [];

  let subFilter: FilterDescriptor[] = [];

  const filters: (CompositeFilterDescriptor | FilterDescriptor)[] | undefined =
    dataState.filter?.filters;

  filters?.map((x: any) => {
    const props = new Map<string, FilterDescriptor[]>(Object.entries(x));
    const newFilters: FilterDescriptor[] = props.get('filters') || [];
    const nodeTypeFilterDescriptor: FilterDescriptor[] = [];
    const nodeTypeFilter: CompositeFilterDescriptor = {
      logic: 'or',
      filters: nodeTypeFilterDescriptor,
    };

    newFilters.map(a => {
      if (Array.isArray(a.value)) {
        for (let i = 0; i < a.value.length; i++) {
          const newValue: FilterDescriptor = {
            field: a?.field,
            operator: a?.operator,
            value: a?.value[i],
          };
          nodeTypeFilterDescriptor.push(newValue);
        }
      }
    });

    if (x.field === 'LastHeartbeat') {
      filter.push(x);
    } else {
      newFilters.map(a => {
        if (Array.isArray(a.value)) {
          filter.push(nodeTypeFilter);
        } else filter.push(x);
      });
    }
  });

  const newFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: filter,
  };

  if (subFilter.length > 0)
    newFilter.filters.push({
      logic: 'or',
      filters: subFilter,
    });

  const newDataStateValue: State = {
    ...dataState,
    filter: newFilter,
  };

  const newDataState = process(data, newDataStateValue);

  setGroupIds({ data: newDataState.data, group: newDataStateValue.group });
  return newDataState;
};

interface Props {
  setLastRefreshQuery: (lastRefreshDate: Date) => void;
}

const ProductsHealthGrid: React.FC<Props> = ({
  setLastRefreshQuery,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );
  const [uniqueKeyOnRefresh, setUniqueKeyOnRefresh] = React.useState<Date>(
    new Date(),
  );
  const [activeThreshold, setActiveThreshold] = React.useState<number>(
    MAX_DAYS_FOR_ONLINE_STATUS,
  );

  const [selectedNodeId, setSelectedNodeId] = React.useState<number | null>(
    null,
  );
  const [selectedMachineName, setSelectedMachineName] =
    React.useState<string>('');
  const [showPackageHealthDialog, setShowPackageHealthDialog] =
    React.useState<boolean>(false);

  const [csvData, setCSVData] = React.useState<NodeDeploymentInfoCSV[]>([]);

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const dataState: State = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.productsHealthGridDataState,
  );
  const isFetchingNodeDeploymentInfo = useSelector(
    (state: ApplicationState) => state.productsReducer.isFetching,
  );
  const nodeDeploymentInfo = useSelector(
    (state: ApplicationState) => state.productsReducer.nodeDeploymentInfo,
  );
  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const status = GetString('STATUS');

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(nodeDeploymentInfo, dataState),
  );

  useEffect(() => {
    setResultState(processWithGroups(nodeDeploymentInfo, dataState));
  }, [nodeDeploymentInfo]);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const result = processWithGroups(nodeDeploymentInfo, event.dataState);

    saveProductsHealthGridState(dispatch, event.dataState);
    setResultState(result);
  };

  const handleFetchNodeDeploymentInfo = () => {
    let newDataState: State = {
      skip: 0,
      take: dataState.take,
      sort: dataState.sort,
      group: dataState.group,
    };
    setUniqueKeyOnRefresh(new Date());
    saveProductsHealthGridState(dispatch, newDataState);
    processFetchNodeDeploymentInfo();
  };

  useEffect(() => {
    processFetchNodeDeploymentInfo();
  }, []);

  const processFetchNodeDeploymentInfo = () => {
    getNodeDeploymentInfo(dispatch);
    setLastRefreshQuery(new Date());
  };

  const getCSVFileName = () => {
    return `ProductsHealth_Status_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    let result: NodeDeploymentInfoCSV[] = [];

    if (Array.isArray(nodeDeploymentInfo)) {
      nodeDeploymentInfo.forEach(node => {
        result.push({
          Division: node.Division,
          StoreNumber: node.SiteNumber,
          Cluster: node.NodeGroup,
          LaneNumber: node.LaneNumber,
          MachineName: node.MachineName?.toUpperCase() ?? '',
          LastHeartbeat: getLocalDateTime(node.LastHeartbeat),
          Status:
            node.Status === ApiEnums.ProductDeploymentStatus.Healthy
              ? 'Healthy'
              : 'Error',
        });
      });
    }

    setCSVData(result);
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  const onChangeActiveThreshold = (event: any) => {
    setActiveThreshold(event.target.value);
  };

  const openPackageHealthDialog = (dataItem: any): void => {
    setSelectedNodeId(dataItem.NodeId);
    setSelectedMachineName(dataItem.MachineName ?? '');
    setShowPackageHealthDialog(true);
  };

  const closePackageHealthDialog = (): void => {
    setShowPackageHealthDialog(false);
    setSelectedNodeId(null);
    setSelectedMachineName('');
  };

  const ProductsHealthCell = (props: GridCellProps): JSX.Element => {
    const status = props.dataItem?.Status as ApiEnums.ProductDeploymentStatus;

    return (
      <td style={{ textAlign: 'center' }}>
        {status === ApiEnums.ProductDeploymentStatus.Healthy && (
          <CheckCircleIcon
            style={{
              color: '#37b400',
              fontSize: 24,
            }}
          />
        )}

        {status === ApiEnums.ProductDeploymentStatus.Error && (
          <CancelIcon
            style={{
              color: '#f31700',
              fontSize: 24,
            }}
          />
        )}
      </td>
    );
  };

  const ViewPackagesCell = (props: GridCellProps): JSX.Element => {
    const dataItem = props.dataItem;

    return (
      <td>
        <Button
          themeColor={'primary'}
          type="button"
          onClick={() => openPackageHealthDialog(dataItem)}
          disabled={!dataItem?.NodeId}
        >
          {GetString('VIEW')}
        </Button>
      </td>
    );
  };

  const DateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.LastHeartbeat &&
      dataItem.LastHeartbeat != '0001-01-01T00:00:00'
    ) {
      if (getDaysDiff(dataItem.LastHeartbeat) < activeThreshold) {
        return (
          <td style={{ backgroundColor: 'rgb(55, 180, 0,0.32)' }}>
            {getLocalDateTime(dataItem.LastHeartbeat)}
          </td>
        );
      } else {
        return (
          <td style={{ backgroundColor: 'rgb(243, 23, 0, 0.32)' }}>
            {getLocalDateTime(dataItem.LastHeartbeat)}
          </td>
        );
      }
    }

    return <td></td>;
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="LastHeartbeat"
      />
    );
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}></IntlProvider>
        <CSVLink
          data={csvData}
          ref={csvLinkRef}
          filename={getCSVFileName() + '.csv'}
        />
        <ProductsHealthFilter activeThreshold={activeThreshold} />
        {isFetchingNodeDeploymentInfo && LoadingPanel}
        <Grid
          key={`${uniqueKeyOnRefresh}`}
          style={{ height: '73vh' }}
          data={resultState}
          {...dataState}
          onDataStateChange={dataStateChange}
          resizable={true}
          sortable={true}
          filterable={true}
          fixedScroll={false}
          pageable={{
            buttonCount: 4,
            pageSizes: [100, 500, 1000, 5000],
          }}
          groupable={true}
          reorderable={true}
          cells={{
            headerCell: HeaderCustomCell,
            data: CustomCell,
          }}
        >
          <GridToolbar>
            <div className={classes.toolbarButtonContainer}>
              <div className={classes.toolbarButtonGrid}>
                <Button
                  onClick={handleFetchNodeDeploymentInfo}
                  type="submit"
                  className={classes.toolbarButton}
                >
                  {GetString('RUN_QUERY')}
                </Button>
                <Button
                  onClick={processExportCSV}
                  type="submit"
                  className={classes.toolbarButton}
                >
                  {GetString('EXPORT_TO_CSV')}
                </Button>
              </div>
              <div className={classes.reportingThresholdDropdownContainer}>
                <Label className={classes.reportingThresholdDropdownLabel}>
                  {GetString('REPORTING_THRESHOLD_DAYS')}:
                </Label>
                <DynamicDropDownList
                  onChange={onChangeActiveThreshold}
                  data={ACTIVE_THRESHOLD_DATA}
                  value={activeThreshold}
                  width={150}
                />
              </div>
            </div>
          </GridToolbar>
          <GridColumn
            {...columnProps('Division')}
            title={GetString('COLUMN_TITLE_DIVISION')}
            width="110px"
            columnMenu={ColumnMenu}
            filterable={false}
          />
          <GridColumn
            {...columnProps('SiteNumber')}
            title={GetString('COLUMN_TITLE_STORE')}
            width="110px"
            columnMenu={ColumnMenu}
            filterable={false}
          />
          <GridColumn
            {...columnProps('NodeGroup')}
            title={GetString('COLUMN_TITLE_CLUSTER')}
            width="110px"
            columnMenu={ColumnMenu}
            filterable={false}
          />
          <GridColumn
            {...columnProps('LaneNumber')}
            title={GetString('COLUMN_TITLE_LANE')}
            width="110px"
            columnMenu={ColumnMenu}
            filterable={false}
          />
          <GridColumn
            {...columnProps('MachineName')}
            title={GetString('COLUMN_TITLE_MACHINE_NAME')}
            width="155px"
            cells={{ data: UpperCaseStringCell }}
            columnMenu={ColumnMenu}
            filterable={false}
          />
          <GridColumn
            title={GetString('VIEW')}
            width="120px"
            filterable={false}
            sortable={false}
            cells={{ data: ViewPackagesCell }}
          />
          <GridColumn
            {...columnProps('LastHeartbeat')}
            title={GetString('COLUMN_TITLE_LANE_UPDATED')}
            width="230px"
            cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="Status"
            title={GetString('COLUMN_TITLE_PRODUCTS_HEALTH')}
            width="150px"
            sortable={true}
            filterable={false}
            cells={{ data: ProductsHealthCell }}
          />
        </Grid>

        <ProductPackagesHealthDialog
          visible={showPackageHealthDialog}
          nodeId={selectedNodeId}
          machineName={selectedMachineName}
          onClose={closePackageHealthDialog}
        />
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ProductsHealthGrid;
